// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __decorate } from 'tslib';
import { html } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { customElement, property } from 'lit/decorators.js';
import '../../styles';
import { CheckboxToggleMixin } from '../../utils/checkbox-toggle-mixin.js';
import { ControlStateMixin } from '../../utils/control-state-mixin.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import {
  dispatchAttributeChangedEventTypesafe,
  event,
  ShuiLitElement,
} from '../../utils/event-decorator.js';
import { FocusBlurMixin } from '../../utils/focus-blur-mixin.js';
import { extendInteractiveArea } from '../../utils/interactive-area.js';
import toggleStyles from './sh-toggle.lit.scss.js';
/**
 * @fires label-changed {LabelPropertyChangedEvent}- *hide  emitted when label property changes
 */
let SHToggle = class SHToggle extends CheckboxToggleMixin(
  ControlStateMixin(FocusBlurMixin(ShuiLitElement))
) {
  static get styles() {
    return [toggleStyles, extendInteractiveArea()];
  }
  render() {
    return html`
      <input
        type="checkbox"
        role="switch"
        id="standard-idle"
        class="toggle focus-item"
        ?checked="${this.active}"
        aria-label="${ifDefined(!this.label ? this.accessibleLabel : undefined)}"
        aria-labelledby="${ifDefined(this.label ? 'toggleLabel' : undefined)}"
        tabindex="${this.disabled ? '-1' : '0'}"
      />
      ${this.label ? html` <label id="toggleLabel" class="toggle-label">${this.label}</label>` : ''}
    `;
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    dispatchAttributeChangedEventTypesafe(this, changedProperties, ['label']);
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.handleSpaceKeydown);
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
  }
  handleSpaceKeydown(event) {
    if (event.code === 'Space') {
      event.preventDefault(); // Prevent the default action (checkbox toggle)
      event.stopPropagation(); // Stop event propagation to avoid interference with other handlers
    }
  }
  _spaceKeyAction() {
    super._spaceKeyAction();
    this.handleClick();
  }
};
__decorate([event()], SHToggle.prototype, 'labelChangedEvent', void 0);
__decorate([property({ type: String, reflect: true })], SHToggle.prototype, 'label', void 0);
__decorate(
  [property({ type: String, attribute: 'accessible-label', reflect: true })],
  SHToggle.prototype,
  'accessibleLabel',
  void 0
);
SHToggle = __decorate([customElement('sh-toggle')], SHToggle);
export { SHToggle };
